import { getAccessToken, setAccessToken } from "../hooks/useAuth";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Deduplicates concurrent refresh calls — all 401s share the same in-flight refresh
let _refreshPromise = null;

async function doRefresh() {
  const res = await fetch(`${API}/api/auth/refresh`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error("refresh_failed");
  const { accessToken } = await res.json();
  setAccessToken(accessToken);
  return accessToken;
}

/**
 * Drop-in replacement for fetch() that:
 *   1. Adds Authorization + Content-Type headers automatically
 *   2. On 401, silently refreshes the access token and retries once
 *   3. On refresh failure, dispatches "auth:expired" and returns the 401 response
 *
 * url may be a full URL or a path like "/api/foo" (auto-prefixed with REACT_APP_API_URL)
 */
export async function fetchWithAuth(url, options = {}) {
  const fullUrl = url.startsWith("http") ? url : `${API}${url}`;

  const makeRequest = (token) =>
    fetch(fullUrl, {
      credentials: "include",
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

  let res = await makeRequest(getAccessToken());
  if (res.status !== 401) return res;

  // 401 — attempt one silent token refresh
  try {
    if (!_refreshPromise) {
      _refreshPromise = doRefresh().finally(() => { _refreshPromise = null; });
    }
    const newToken = await _refreshPromise;
    res = await makeRequest(newToken);
  } catch {
    setAccessToken(null);
    window.dispatchEvent(new CustomEvent("auth:expired"));
    // Return a synthetic 401 so callers can handle it gracefully
    return new Response(
      JSON.stringify({ error: "Session expired — please sign in again" }),
      { status: 401, headers: { "Content-Type": "application/json" } }
    );
  }

  return res;
}
